import * as rrd from 'react-router-dom';
import { useEffect, useCallback, useMemo } from 'react';

import {
  SingleConverter,
  AccountConverter,
  ProfileConverter,
} from '@/containers';
import { slugToPlatform } from '@/utils';
import { TABS, DEFAULT_TAB } from '@/constants';

const { useParams, useNavigate } = rrd.default ?? rrd;

const TAB_META = {
  account: {
    label: 'Account',
    Component: AccountConverter,
    description: 'Transfer playlists from an account',
  },
  profile: {
    label: 'Profile',
    Component: ProfileConverter,
    description: 'Transfer public playlists from a profile',
  },
  single: {
    label: 'Single',
    Component: SingleConverter,
    description: 'Transfer a playlist using its sharable URL',
  },
};

const Converter = () => {
  const navigate = useNavigate();

  const { tab, conversion } = useParams();

  const isConversionPair = conversion?.includes('-to-');
  const activeTab = isConversionPair ? tab : conversion;

  // 1. Identify the source platform via slug
  const source = slugToPlatform(
    isConversionPair ? conversion.split('-to-')[0] : null
  );

  // 2. Filter tabs: memoized for performance
  // Removes 'profile' specifically for Apple Music
  const visibleTabs = useMemo(() => {
    return TABS.filter(
      (tab) => !(tab === 'profile' && source?.toLowerCase() === 'apple')
    );
  }, [source]);

  useEffect(() => {
    const basePath = isConversionPair ? `/convert/${conversion}` : '/convert';

    // 3. Security Check: Redirect if user is on a forbidden tab or an invalid tab
    const isForbidden =
      activeTab === 'profile' && source?.toLowerCase() === 'apple';

    const isInvalid = !TABS.includes(activeTab);

    if (isForbidden || isInvalid) {
      navigate(`${basePath}/${DEFAULT_TAB}`, { replace: true });
    }
  }, [source, navigate, activeTab, conversion, isConversionPair]);

  const switchTab = useCallback(
    (targetTab) => {
      if (targetTab === activeTab) return;

      const basePath = isConversionPair ? `/convert/${conversion}` : '/convert';

      navigate(`${basePath}/${targetTab}`);
    },
    [navigate, activeTab, conversion, isConversionPair]
  );

  return (
    <div className="row justify-content-center">
      <div className="col-lg-8">
        {/* Navigation Tabs */}
        <ul role="tablist" id="converterTabs" className="nav nav-tabs mb-4">
          {visibleTabs.map((tab) => (
            <li className="nav-item" role="presentation" key={tab}>
              <button
                role="tab"
                type="button"
                id={`${tab}-tab`}
                onClick={() => switchTab(tab)}
                aria-selected={activeTab === tab}
                className={`nav-link ${activeTab === tab ? 'active' : ''}`}
              >
                {TAB_META[tab].label}
              </button>
            </li>
          ))}
        </ul>

        {/* Tab Panes */}
        <div className="tab-content" id="converterTabContent">
          {visibleTabs.map((tab) => {
            const isActive = activeTab === tab;

            const { description, Component } = TAB_META[tab];

            return (
              <div
                id={tab}
                key={tab}
                role="tabpanel"
                className={`tab-pane fade ${isActive ? 'show active' : ''}`}
              >
                <p className="text-muted d-none d-md-block">{description}</p>
                <Component />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Converter;
